import {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { io } from 'socket.io-client';
import { AuthContext } from './AuthContext';
import API_BASE_URL from '../config/api';

export const SocketContext = createContext();

/**
 * Owns the single Socket.IO connection.
 *
 * Mirrors AuthContext's shape deliberately. The socket itself lives in a ref,
 * not state, because it must not trigger re-renders — only genuinely
 * render-affecting facts (connected, error) are state.
 *
 * Snapshots are NOT state. They arrive 20x/sec and go straight to the engine
 * via a subscriber callback; routing them through React would re-render the
 * tree 20 times a second to update a canvas React does not draw.
 */
export function SocketProvider({ children }) {
  const { token } = useContext(AuthContext);
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // No token means no connection attempt — the handshake would be rejected
    // anyway, and retrying would spam the server.
    if (!token) {
      setConnected(false);
      return undefined;
    }

    const socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError(null);
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('connect_error', (err) => {
      setConnected(false);
      // A rejected handshake surfaces here, not as a thrown error.
      setError(err?.message || 'Connection failed');
    });

    // Fan every server event out to whoever subscribed. Keeps this provider
    // ignorant of specific event names.
    socket.onAny((event, payload) => {
      listenersRef.current.forEach((fn) => fn(event, payload));
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [token]);

  const value = useMemo(() => ({
    connected,
    error,

    /** Emit to the server. No-ops when disconnected rather than throwing. */
    emit: (event, payload) => {
      socketRef.current?.emit(event, payload);
    },

    /**
     * Subscribe to all server events. Returns an unsubscribe function.
     * Used by the engine; deliberately not a React state update.
     */
    subscribe: (fn) => {
      listenersRef.current.add(fn);
      return () => listenersRef.current.delete(fn);
    },
  }), [connected, error]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

SocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSocket = () => useContext(SocketContext);
